import React, { useState, useEffect } from 'react';
import { mfaEnroll, mfaEnrollVerify } from '../services/api';
import { Shield, AlertCircle, CheckCircle2, Copy, X } from 'lucide-react';
import './MFAEnrollmentModal.css';

/**
 * MFA Enrollment Modal - Guides user through MFA device enrollment with QR code.
 */
export const MFAEnrollmentModal = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState('name'); // 'name', 'qr', 'verify'
  const [deviceName, setDeviceName] = useState('');
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setStep('name');
      setDeviceName('');
      setQrCode(null);
      setSecret('');
      setVerificationCode('');
      setError(null);
      setRecoveryCodes(null);
    }
  }, [isOpen]);

  const handleStartEnrollment = async (e) => {
    e.preventDefault();
    if (!deviceName.trim()) {
      setError('Device name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await mfaEnroll(deviceName.trim());
      setQrCode(response.data.qr_code);
      setSecret(response.data.secret);
      setStep('qr');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to start enrollment');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setError('Verification code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await mfaEnrollVerify(verificationCode.trim());
      
      // Check if recovery codes were returned
      if (response.data.recovery_codes) {
        setRecoveryCodes(response.data.recovery_codes);
        setStep('recovery');
      } else {
        // Enrollment successful, no recovery codes shown
        onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    // Could show a toast notification here
  };

  const handleCopyRecoveryCodes = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text);
    // Could show a toast notification here
  };

  const handleDownloadRecoveryCodes = () => {
    if (!recoveryCodes || recoveryCodes.length === 0) return;
    const content = recoveryCodes.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleRecoveryCodesDone = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mfa-enrollment-modal-overlay" onClick={onClose}>
      <div className="mfa-enrollment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mfa-enrollment-header">
          <Shield size={24} />
          <h2>Set Up Multi-Factor Authentication</h2>
          <button className="mfa-enrollment-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mfa-enrollment-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Step 1: Device Name */}
        {step === 'name' && (
          <div className="mfa-enrollment-step">
            <p className="mfa-enrollment-description">
              Enter a name for this device (e.g., "iPhone", "Work Laptop") to help you identify it later.
            </p>
            <form onSubmit={handleStartEnrollment}>
              <div className="form-group">
                <label>Device Name *</label>
                <input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="My iPhone"
                  required
                  autoFocus
                  disabled={loading}
                />
              </div>
              <div className="mfa-enrollment-actions">
                <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Starting...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: QR Code Display */}
        {step === 'qr' && (
          <div className="mfa-enrollment-step">
            <p className="mfa-enrollment-description">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, etc.)
            </p>
            
            {qrCode && (
              <div className="mfa-qr-container">
                <img src={qrCode} alt="MFA QR Code" className="mfa-qr-code" />
              </div>
            )}

            <div className="mfa-secret-container">
              <label>Or enter this code manually:</label>
              <div className="mfa-secret-display">
                <code className="mfa-secret-text">{secret}</code>
                <button
                  type="button"
                  onClick={handleCopySecret}
                  className="mfa-copy-button"
                  title="Copy secret"
                >
                  <Copy size={16} />
                </button>
              </div>
            </div>

            <div className="mfa-enrollment-instructions">
              <h4>Instructions:</h4>
              <ol>
                <li>Open your authenticator app</li>
                <li>Scan the QR code or enter the secret manually</li>
                <li>Enter the 6-digit code from your app below</li>
              </ol>
            </div>

            <form onSubmit={handleVerify}>
              <div className="form-group">
                <label>Verification Code *</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                  disabled={loading}
                  className="mfa-code-input"
                />
                <small>Enter the 6-digit code from your authenticator app</small>
              </div>
              <div className="mfa-enrollment-actions">
                <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading || verificationCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify & Complete'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Recovery Codes */}
        {step === 'recovery' && recoveryCodes && (
          <div className="mfa-enrollment-step">
            <div className="mfa-recovery-success">
              <CheckCircle2 size={24} />
              <h3>MFA Successfully Enabled!</h3>
            </div>
            <p className="mfa-enrollment-description">
              <strong>Important:</strong> Save these recovery codes in a safe place. You can use them to access your account if you lose access to your authenticator device.
            </p>
            <div className="mfa-recovery-codes-container">
              {recoveryCodes.map((code, index) => (
                <div key={index} className="mfa-recovery-code-item">
                  <code>{code}</code>
                </div>
              ))}
            </div>
            <div className="mfa-recovery-actions">
              <button
                type="button"
                onClick={handleDownloadRecoveryCodes}
                className="btn-secondary"
              >
                Download Codes
              </button>
              <button
                type="button"
                onClick={handleCopyRecoveryCodes}
                className="btn-secondary"
              >
                Copy All
              </button>
            </div>
            <div className="mfa-enrollment-actions">
              <button type="button" onClick={handleRecoveryCodesDone} className="btn-primary">
                I've Saved My Codes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
